import { Outlet, NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  TrendingUp,
  TrendingDown,
  Building2,
  Settings,
  Calculator,
  Users,
  ArrowRightLeft
} from 'lucide-react';
import { UserButton } from '@clerk/clerk-react';
import NotificationBell from '@/components/notifications/NotificationBell';
import { useEmpresa } from '@/context/EmpresaContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Navegación principal - Operaciones del día a día
const mainNavItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/operaciones-lp', icon: TrendingUp, label: 'Largo Plazo' },
  { to: '/operaciones-cp', icon: TrendingDown, label: 'Corto Plazo' },
  { to: '/proyecciones', icon: Calculator, label: 'Proyecciones' },
];

// URL de FinReport (Sumas y Saldos). Misma instancia Clerk (clerk.pflabs.es),
// por lo que la sesión se mantiene al cambiar de app.
const FINREPORT_URL = process.env.REACT_APP_FINREPORT_URL || 'https://app.pflabs.es';

// Navegación de administración - Gestión de empresas y configuración
const adminNavItems = [
  { to: '/empresas', icon: Building2, label: 'Empresas' },
  { to: '/configuracion', icon: Settings, label: 'Configuración' },
];

export default function Layout() {
  const { empresas, selectedEmpresa, setSelectedEmpresa, loading } = useEmpresa();

  const handleEmpresaChange = (empresaId) => {
    const empresa = empresas.find(e => e.id === empresaId);
    setSelectedEmpresa(empresa);
  };

  return (
    <div className="flex h-screen bg-background" data-testid="main-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col" data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <img src="/logopf.png" alt="PF Labs" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="font-bold text-lg font-['Outfit']">Pool Bancario</h1>
              <p className="text-xs text-slate-400">Gestión Financiera</p>
            </div>
          </div>
        </div>

        {/* Empresa Selector */}
        <div className="p-4 border-b border-slate-800">
          <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
            Empresa
          </label>
          <Select 
            value={selectedEmpresa?.id || ''} 
            onValueChange={handleEmpresaChange}
            disabled={loading || empresas.length === 0}
          >
            <SelectTrigger 
              className="w-full bg-slate-800 border-slate-700 text-white"
              data-testid="empresa-selector"
            >
              <SelectValue placeholder="Seleccionar empresa" />
            </SelectTrigger>
            <SelectContent>
              {empresas.map((empresa) => (
                <SelectItem key={empresa.id} value={empresa.id}>
                  {empresa.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {empresas.length === 0 && !loading && (
            <p className="text-xs text-slate-500 mt-2">
              Crea una empresa para empezar
            </p>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto" data-testid="nav-menu">
          {/* Sección Principal */}
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-4">
            Principal
          </p>
          <div className="space-y-1 mb-6">
            {mainNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* Separador visual */}
          <div className="border-t border-slate-700 my-4"></div>

          {/* Sección Administración */}
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 px-4 flex items-center gap-2">
            <Users className="w-3 h-3" />
            Administración
          </p>
          <div className="space-y-1">
            {adminNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* App Switcher */}
        <div className="p-4 border-t border-slate-800">
          <a
            href={FINREPORT_URL}
            className="sidebar-link"
            title="Cambiar a FinReport — Sumas y Saldos"
            data-testid="switch-finreport"
          >
            <ArrowRightLeft className="w-5 h-5" />
            <span className="flex-1">FinReport</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Cambiar</span>
          </a>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800">
          <p className="text-xs text-slate-500 text-center">© 2025</p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="glass-header h-16 flex items-center justify-between px-6">
          <div />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <UserButton afterSignOutUrl="/sign-in" />
          </div>
        </header>

        <div className="p-8 animate-fade-in-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
